import React, { useEffect, useRef, useState } from 'react';
import { adminListUsers } from '../api';
import type { User } from '../types';

const DEBOUNCE_MS = 250;
const MAX_SUGGESTIONS = 10;
const SEARCH_PAGE_SIZE = 20;

interface UserSearchInputProps {
  placeholder?: string;
  ariaLabel?: string;
  /** Omit this user from suggestions (e.g. the project's current owner). */
  excludeUserId?: string;
  /** Fires with the picked user on selection, or null when the field is cleared/edited. */
  onSelect: (user: User | null) => void;
  style?: React.CSSProperties;
  className?: string;
}

// UserSearchInput replaces "load every user into a datalist" with a debounced
// server-side username search (mirrors the account-group member search). The
// parent owns the resolved selection; this component only turns keystrokes into
// a (debounced query -> suggestions -> onSelect) flow. onSelect(null) is emitted
// whenever the text no longer matches the last picked user, so a stale id can
// never linger after the admin edits the field.
export default function UserSearchInput({
  placeholder,
  ariaLabel,
  excludeUserId,
  onSelect,
  style,
  className = 'input',
}: UserSearchInputProps) {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<readonly User[]>([]);
  const [open, setOpen] = useState(false);
  const selectedUsernameRef = useRef('');

  useEffect(() => {
    const q = query.trim();
    if (!q) {
      setSuggestions([]);
      return;
    }
    // Already showing the picked user's name verbatim — no need to re-query.
    if (q === selectedUsernameRef.current) {
      setSuggestions([]);
      return;
    }
    let ignore = false;
    const handle = setTimeout(() => {
      adminListUsers(1, SEARCH_PAGE_SIZE, q)
        .then((res) => {
          if (ignore) return;
          const items = (res.items || []).filter((u) => u.id !== excludeUserId).slice(0, MAX_SUGGESTIONS);
          setSuggestions(items);
        })
        .catch(() => {
          if (!ignore) setSuggestions([]);
        });
    }, DEBOUNCE_MS);
    return () => {
      ignore = true;
      clearTimeout(handle);
    };
  }, [query, excludeUserId]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const v = e.target.value;
    setQuery(v);
    setOpen(true);
    // Any edit that diverges from the picked user invalidates the selection.
    if (v !== selectedUsernameRef.current) {
      selectedUsernameRef.current = '';
      onSelect(null);
    }
  };

  const handlePick = (user: User): void => {
    selectedUsernameRef.current = user.username;
    setQuery(user.username);
    setSuggestions([]);
    setOpen(false);
    onSelect(user);
  };

  return (
    <div style={{ position: 'relative', ...style }}>
      <input
        className={className}
        aria-label={ariaLabel}
        placeholder={placeholder}
        value={query}
        autoComplete="off"
        onChange={handleChange}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        style={{ width: '100%', boxSizing: 'border-box' }}
      />
      {open && suggestions.length > 0 && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 1100,
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: 6,
            marginTop: 2,
            maxHeight: 200,
            overflowY: 'auto',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          }}
        >
          {suggestions.map((u) => (
            <div
              key={u.id}
              onMouseDown={() => handlePick(u)}
              style={{ padding: '0.4rem 0.75rem', cursor: 'pointer', fontSize: '0.85rem', color: 'var(--text)' }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg-2)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
            >
              {u.username}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
